/**
 * Session lifecycle and the credit ledger's transfer shape
 * (architecture §8.2, §8.3, §8.5).
 *
 * `AwaitingRoll` is a sub-state of play, not a pause: the session clock and the credit
 * clock keep running, world time does not advance, and only the throw, the wake
 * phrase, a switch to Auto or a socket close leaves it.
 */
import { z } from 'zod';
import { Locale, Measures, RollKind, RollMode } from './primitives.js';
/* ------------------------------------------------------- state machine */
export const SessionState = z.enum([
    'lobby',
    'starting',
    'active',
    'awaiting_roll',
    'paused',
    'wrap_up',
    'ending',
    'crashed',
    'recovering',
    'settled',
    'refunded',
]);
/** The legal transitions of §8.2, as data, so the actor and its tests share one table. */
export const SESSION_TRANSITIONS = Object.freeze({
    lobby: ['starting'],
    starting: ['active', 'lobby'],
    active: ['active', 'awaiting_roll', 'paused', 'wrap_up', 'ending', 'crashed'],
    awaiting_roll: ['active', 'paused'],
    paused: ['active', 'ending'],
    wrap_up: ['ending', 'crashed'],
    ending: ['settled'],
    crashed: ['recovering'],
    recovering: ['active', 'refunded'],
    settled: [],
    refunded: [],
});
export function canTransition(from, to) {
    return SESSION_TRANSITIONS[from].includes(to);
}
/** Session-clock alarms in minutes (§8.2 WrapUp, §3.5 session skeleton). */
/** The sittings a player can book (ADR-087): half an hour, an hour, two hours. */
export const SessionLength = z.union([z.literal(30), z.literal(60), z.literal(120)]);
/** ADR-088: time skips are a campaign setting, never a narrator's choice. */
export const TimeSkips = z.enum(['never', 'journeys']);
/**
 * The session skeleton scaled to its length (ADR-087): converge at five sixths,
 * wrap up at eleven twelfths, no new threads just after, a cliffhanger in the last
 * sixtieth, the hard stop at the length. The two-hour numbers are §3.5's own.
 */
export function sessionAlarmsFor(lengthMinutes) {
    const L = Math.max(10, lengthMinutes);
    return Object.freeze({
        converge: Math.round((L * 100) / 120),
        wrapUp: Math.round((L * 110) / 120),
        noNewThreads: Math.round((L * 112) / 120),
        cliffhanger: Math.round((L * 118) / 120),
        hardStop: L,
        pauseGraceMinutes: 10,
        recoveryWindowMinutes: 10,
    });
}
export const SESSION_ALARMS = Object.freeze({
    converge: 100,
    noNewThreads: 112,
    wrapUp: 110,
    cliffhanger: 118,
    hardStop: 120,
    /** A `Paused` session ends normally after this many minutes. */
    pauseGraceMinutes: 10,
    /** A `Crashed` session that no pod accepts within this window is `Refunded`. */
    recoveryWindowMinutes: 10,
});
/**
 * Auto-roll timeouts (ADR-122): the tray waits **ten seconds**, in combat and out, then rolls
 * for the player; the GM's spoken nudge comes at four. One number, kept under both names so
 * the engine's fallback keeps its shape — ADR-061's 30 s / 20 s pair never both ran in
 * production, since a session-level timeout is flat (`autoAfterMs`). The configurable range is
 * 10–120 s; 0 means "nunca".
 */
/**
 * ADR-128 decision 3 (#49): how long a held recording's transcript sits in the field before it is
 * sent as it stands. The clock runs on the pod, as the tray's does (ADR-122); a click, a tap or a
 * key in the field stops it. A locked recording has no clock.
 */
export const TRANSCRIPT_PREVIEW_MS = 2_000;
/**
 * ADR-128 decision 4 (amended 2026-10-30): a locked recording is one message made of segments —
 * paused and resumed, as a WhatsApp voice note is — and each segment's transcript is appended to
 * whatever the field holds. The seam is one space: the field's trailing whitespace and the
 * segment's surrounding whitespace go, and nothing else is touched — punctuation is the
 * transcriber's and stays as it came, on both sides, so "…the door." + "Then I listen." reads as
 * two sentences and "…the door" + "then I listen" stays as it was spoken. Shared by the pod,
 * which keeps its copy of the message to recognise an unedited send, and by the client, whose
 * field is the message. Pure, so both suites hold it.
 */
export function appendSegment(field, segment) {
    const head = field.trimEnd();
    const tail = segment.trim();
    if (head.length === 0)
        return tail;
    if (tail.length === 0)
        return head;
    return `${head} ${tail}`;
}
export const ROLL_TIMEOUT_DEFAULTS_MS = Object.freeze({
    idlePromptMs: 4_000,
    outOfCombatMs: 10_000,
    inCombatMs: 10_000,
    minConfigurableMs: 10_000,
    maxConfigurableMs: 120_000,
});
/* ------------------------------------------------------------ settings */
export const NarrationProfile = z.enum(['quality', 'balanced', 'hedge']);
export const StorytellerPreset = z.enum(['tense', 'calm', 'unpredictable']);
export const RollModePreset = z.enum(['quick', 'my_dice', 'all_in_my_hand']);
/**
 * Content policy, as pack/campaign data rather than prose in a prompt (ADR-063,
 * founder decision 2026-09-04: **18+ only**). Two axes, both build-linted the way
 * `age-guard` and `peak-coverage` already are. Three ceilings are *not* expressible
 * here on purpose — gratuitous gore, depiction of animal cruelty and sexualisation of
 * a minor-coded character are provider policy, not product policy, and stay
 * unconditional rejects that no value of this object can raise.
 */
export const ContentTier = z.object({
    /** 18+ permits `shown`: an execution or a torture scene may be narrated as completed. */
    violenceCompletion: z.enum(['threat', 'shown']).default('shown'),
    /** On-screen death of a named NPC, including a child NPC as tragedy — never sexualised. */
    deathNarration: z.enum(['offscreen', 'onscreen']).default('onscreen'),
});
/**
 * The player-facing age floor (ADR-063). The gate that enforces it is layered and
 * lives in the API (§8.6); this constant is the number both the gate and the copy
 * are written against.
 */
export const MIN_PLAYER_AGE_YEARS = 18;
/**
 * Whether a session uses the voice node at all (founder, 2026-09-07; ADR-083). `off` is
 * text mode by choice, not by outage: the client opens no WebRTC leg and the actor sends
 * the node nothing for the session — no `voice_state`, no `say` — so no sentence is
 * synthesised and nothing reaches a speech provider on the session's behalf.
 */
export const VoiceSetting = z.enum(['on', 'off']);
export const SessionSettings = z.object({
    locale: Locale,
    profile: NarrationProfile,
    storyteller: StorytellerPreset,
    rollModes: z.record(RollKind, RollMode),
    rollTimeoutMs: z.number().int().min(0).max(120_000),
    /**
     * ADR-119: feet or metres, for everything the table is shown. Defaulted by the language
     * a table plays in — English tables have always read feet and Portuguese ones metres —
     * and changed from there whenever the player says so.
     */
    measures: Measures.default('feet'),
    /** "CDs abertas": off by default, so the tray reads "vs CD ?" until the die lands. */
    openDcs: z.boolean().default(false),
    /** "dados cinematográficos": the voice drops numbers; the log never does. */
    cinematicDice: z.boolean().default(false),
    /** "dados do inimigo": a 400 ms tumble in the enemy's colour before the engine line (§5.4, §6.6); off by default. */
    showEnemyDice: z.boolean().default(false),
    /** "números visíveis": enemy AC and HP on the board. */
    showEnemyNumbers: z.boolean().default(false),
    mic: z.enum(['ptt', 'open']).default('ptt'),
    /** ADR-087: how long tonight is; the skeleton, the director's cadence and the prep scale to it. */
    lengthMinutes: SessionLength.default(60),
    /** ADR-088: whether the engine may grant a time skip. `journeys`: a known road may be crossed in one command, hazards and nightfall rolled per leg. `never`: exit by exit. Pre-determined in the lobby; the narrator never decides it. */
    timeSkips: TimeSkips.default('journeys'),
    /** ADR-083: `off` keeps the session away from the voice node entirely. */
    voice: VoiceSetting.default('on'),
    /** ADR-112: whether session one opens on the campaign's prologue; off, it opens straight on the narrator's first scene. */
    prologue: z.boolean().default(true),
    reducedMotion: z.boolean().default(false),
    /** ADR-063. Defaults are the 18+ tier; a campaign may only narrow, never widen. */
    contentTier: ContentTier.default({}),
    /**
     * "Lines" from the session-zero checklist: storylet and peak tags the director
     * excludes from selection outright, filtered server-side the way the PI blocklist
     * filters terms — not an instruction appended to a prompt (ADR-063).
     */
    hardExclusions: z.array(z.string()).default([]),
    /**
     * The game master persona chosen with the campaign (ADR-080): a named manner and
     * a voice. Absent means the pack's own persona text. A persona changes the
     * telling and never the game — no DC, roll, enemy or content tier reads it.
     */
    personaId: z.string().optional(),
});
/* -------------------------------------------------------- session header */
export const SessionHeader = z.object({
    sessionId: z.string().uuid(),
    campaignId: z.string().uuid(),
    userId: z.string().uuid(),
    characterId: z.string(),
    sessionNumber: z.number().int().min(1),
    /** Content-addressed pack bundle this session was played against (§9.1). */
    packBundleHash: z.string(),
    /** e.g. 'srd-5.2.1+everwake.2026.10' — a replay uses the rules it was played under. */
    ruleVersion: z.string(),
    /** sha256(sessionSeed), published at `Starting` (§5.4). */
    seedCommitment: z.string().regex(/^[0-9a-f]{64}$/),
    settings: SessionSettings,
    state: SessionState,
    seq: z.number().int().nonnegative(),
    startedAt: z.string().datetime(),
    heartbeatAt: z.string().datetime(),
});
/** One row of the `events` table (§8.3). */
export const EventLogRow = z.object({
    sessionId: z.string().uuid(),
    seq: z.number().int().nonnegative(),
    ts: z.string().datetime(),
    kind: z.string(),
    /** `player:<id> | npc:<id> | gm | engine | director | governor | system` */
    actorId: z.string(),
    /** `cmd:<seq> | tool:<name>@<seq> | tick:period | tick:day | catchup | timer:110m | roll:<roll_id>` */
    cause: z.string(),
    payload: z.unknown(),
});
/* ------------------------------------------------------- credit ledger */
export const LedgerAccountKind = z.enum([
    'user_wallet',
    'revenue_sessions',
    'revenue_addons',
    'liability_unspent',
    'promo_expense',
    'refunds',
    'chargebacks',
]);
export const TransferState = z.enum(['pending', 'posted', 'voided']);
/**
 * A two-phase transfer: **pending** at `Starting`, **posted** (possibly for less) at
 * `Settled`, **voided** by the sweeper on failure. Immutable and idempotent on
 * `idempotencyKey` — a retry with the same session id returns the existing hold.
 */
export const LedgerTransfer = z.object({
    id: z.string(),
    /** session id | stripe event id | refund id | promo grant id. */
    idempotencyKey: z.string().min(1),
    debitAccount: z.number().int(),
    creditAccount: z.number().int(),
    /** Whole credits; always > 0. */
    amount: z.number().int().min(1),
    state: TransferState,
    /** ≤ `amount`, set when posted pro-rata after a server-observed early end. */
    postedAmount: z.number().int().min(0).nullable(),
    /** Pending transfers only; the 3-hour TTL covers play, grace and recovery. */
    expiresAt: z.string().datetime().nullable(),
    sessionId: z.string().uuid().nullable(),
    purchaseId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    settledAt: z.string().datetime().nullable(),
});
/** A 2-hour session is a 10-credit hold (§8.5). */
export const SESSION_CREDIT_COST = 10;
export const SESSION_HOLD_TTL_HOURS = 3;
/**
 * Pro-rata posting for **server-observed** early ends only. Player-initiated early
 * ends post in full, and character death never refunds (§1.2, §8.5).
 */
export function proRataPostedCredits(playedMinutes) {
    if (playedMinutes < 5)
        return 0;
    return Math.min(SESSION_CREDIT_COST, Math.max(2, Math.ceil((Math.ceil(playedMinutes / 15) * 5) / 4)));
}
/** Whether an early end is the server's fault, and so eligible for pro-rata posting. */
export const EarlyEndCause = z.enum([
    'server_abort',
    'provider_outage',
    'voice_and_text_down',
    'player_left',
    'grace_expired',
    'player_asked',
    'character_death',
]);
export const SERVER_OBSERVED_CAUSES = Object.freeze(['server_abort', 'provider_outage', 'voice_and_text_down']);
//# sourceMappingURL=session.js.map